// src/components/StatsModal.js
import React, { useState, useMemo } from 'react';
import { IoFootstepsOutline, IoLibrary } from 'react-icons/io5';
import CountdownTimer from './CountdownTimer';

const PathModal = ({ path, onClose }) => {
    if (!path) return null;
    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
            onClick={onClose}
        >
            <div 
                className="bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground rounded-lg p-6 w-full max-w-md mx-4 shadow-xl"
                onClick={e => e.stopPropagation()} // Prevent closing when clicking inside
            >
                <h3 className="text-xl font-bold text-center mb-4">WORD PATH</h3>
                <p className="text-center font-mono text-sm break-words">
                    {path.join(' → ')}
                </p>
                <div className="flex justify-center mt-6">
                    <button 
                        onClick={onClose}
                        className="bg-accent dark:bg-dark-accent px-6 py-2 rounded-lg font-bold text-background dark:text-dark-background transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const StatsModal = ({ isOpen, onClose, stats, onArchiveClick }) => {
    const [viewingPath, setViewingPath] = useState(null);
    // Calculate the distribution of the player's best length for all played days.
    const lengthDistribution = useMemo(() => {
        const distribution = {};
        if (!stats || !stats.dailyResults) {
            return distribution;
        }

        // Iterate over all daily results
        Object.values(stats.dailyResults).forEach(result => {
            // We include all games that have a recorded best length, regardless of win/loss.
            if (result.bestLength) {
                const length = result.bestLength;
                distribution[length] = (distribution[length] || 0) + 1;
            }
        });

        return distribution;
    }, [stats]);

    const history = useMemo(() => {
        return Object.entries(stats.dailyResults || {})
            .map(([date, result]) => ({ date, ...result }))
            .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
    }, [stats]);

    if (!isOpen) return null;
    
    const distributionKeys = Object.keys(lengthDistribution).sort((a, b) => a - b);
    const maxCount = Math.max(0, ...Object.values(lengthDistribution));

    const medalCounts = stats.medalCounts || { bronze: 0, silver: 0, gold: 0, platinum: 0 };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4">
            <div className="bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground rounded-lg p-6 w-full max-w-md mx-4 shadow-xl overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">STATISTICS</h2>
                    <button onClick={onClose} className="text-3xl font-light">&times;</button>
                </div>

                {/* Main Stats Grid */}
                <div className="grid grid-cols-3 text-center my-4 gap-2">
                    <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-md">
                        <p className="text-2xl font-bold">{stats.gamesPlayed || 0}</p>
                        <p className="text-xs uppercase tracking-wide">Played</p>
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-md">
                        <p className="text-2xl font-bold">{stats.currentStreak || 0}</p>
                        <p className="text-xs uppercase tracking-wide">Streak</p>
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-md">
                        <p className="text-2xl font-bold">{stats.maxStreak || 0}</p>
                        <p className="text-xs uppercase tracking-wide">Max Streak</p>
                    </div>
                </div>

                 {/* Medal Collection */}
                 <h3 className="text-sm font-bold text-center mt-6 mb-2 uppercase tracking-widest text-gray-500">Medal Collection</h3>
                 <div className="grid grid-cols-4 gap-2 mb-6">
                    <div className="flex flex-col items-center bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg border border-gray-100 dark:border-gray-700">
                        <span className="text-2xl mb-1">🥉</span>
                        <span className="font-bold text-xl">{medalCounts.bronze}</span>
                        <span className="text-[10px] uppercase text-gray-500">Bronze</span>
                    </div>
                    <div className="flex flex-col items-center bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg border border-gray-100 dark:border-gray-700">
                        <span className="text-2xl mb-1">🥈</span>
                        <span className="font-bold text-xl">{medalCounts.silver}</span>
                        <span className="text-[10px] uppercase text-gray-500">Silver</span>
                    </div>
                    <div className="flex flex-col items-center bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg border border-gray-100 dark:border-gray-700">
                        <span className="text-2xl mb-1">🥇</span>
                        <span className="font-bold text-xl">{medalCounts.gold}</span>
                        <span className="text-[10px] uppercase text-gray-500">Gold</span>
                    </div>
                    <div className="flex flex-col items-center bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg border border-gray-100 dark:border-gray-700">
                        <span className="text-2xl mb-1">🏆</span>
                        <span className="font-bold text-xl">{medalCounts.platinum}</span>
                        <span className="text-[10px] uppercase text-gray-500">Best</span>
                    </div>
                 </div>

                {/* Distribution Graph */}
                <h3 className="text-sm font-bold text-center mb-2 uppercase tracking-widest text-gray-500">Best Length Distribution</h3>
                <div className="space-y-2 mb-6">
                    {distributionKeys.length > 0 ? (
                        distributionKeys.map(len => (
                            <div key={len} className="flex items-center text-sm font-bold">
                                <div className="w-4">{len}</div>
                                <div className="flex-grow bg-gray-200 dark:bg-gray-700 h-5 ml-2 rounded-sm overflow-hidden">
                                    <div 
                                        className="bg-accent dark:bg-dark-accent h-5 text-right px-2 text-background dark:text-dark-background flex items-center justify-end" 
                                        style={{ width: `${maxCount > 0 ? (lengthDistribution[len] / maxCount) * 100 : 0}%`, minWidth: '1.5rem' }}
                                    >
                                        {lengthDistribution[len]}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-center text-sm text-gray-500">No games played yet.</p>
                    )}
                </div>

                {/* RECENT HISTORY */}
                <h3 className="text-sm font-bold text-center mt-6 mb-2 uppercase tracking-widest text-gray-500">History</h3>
                <div className="space-y-2 text-sm max-h-60 overflow-y-auto pr-1">
                    {history.length > 0 ? history.map(item => {
                        const word = item.bestWord || item.status;
                        let wordClass = 'font-bold uppercase';
                        if (item.bestWord) {
                            if (item.bestWord.length > 13) {
                                wordClass += ' text-xs tracking-tight';
                            } else if (item.bestWord.length > 9) {
                                wordClass += ' text-sm tracking-normal';
                            } else {
                                wordClass += ' tracking-widest';
                            }
                        } else {
                            wordClass += ' tracking-widest';
                        }
                        
                        // Use medal color if available, or fall back to status
                        let statusColor = '';
                        if (item.medal === 'platinum' || item.status === 'won') statusColor = 'text-purple-500 font-bold';
                        else if (item.medal === 'gold') statusColor = 'text-yellow-500';
                        else if (item.medal === 'silver') statusColor = 'text-gray-400';
                        else if (item.medal === 'bronze') statusColor = 'text-orange-500';

                        return (
                            <div key={item.date} className="grid grid-cols-3 text-center p-2 bg-gray-100 dark:bg-gray-700 rounded-md items-center">
                                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{item.date}</span>
                                <span className="flex items-center justify-center space-x-2">
                                    <span>{item.starterWord ? `${item.starterWord} → ${item.bestLength}` : `Best: ${item.bestLength}`}</span>
                                    {item.wordChain && (
                                        <button 
                                            onClick={() => setViewingPath(item.wordChain)} 
                                            className="flex items-center justify-center w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                                            aria-label="Show path"
                                        >
                                            <IoFootstepsOutline size="12" />
                                        </button>
                                    )}
                                </span>
                                <span className={`${wordClass} ${statusColor}`}>
                                    {word}
                                </span>
                            </div>
                        );
                    }) : <p className="text-center text-sm text-gray-500">No history yet.</p>}
                </div>

                {/* Countdown Timer ONLY */}
                <div className="flex flex-col justify-center items-center mt-6 space-y-4">
                    <CountdownTimer />
                    
                    {onArchiveClick && (
                        <button 
                            onClick={onArchiveClick}
                            className="flex items-center justify-center space-x-2 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                        >
                            <IoLibrary />
                            <span>View Archive</span>
                        </button>
                    )}
                </div>
            </div>
            <PathModal path={viewingPath} onClose={() => setViewingPath(null)} />
        </div>
    );
};

export default StatsModal;
